import React, { useEffect, useState, useRef } from 'react';
import dayjs from 'dayjs';
import parse from 'html-react-parser';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import relativeTime from 'dayjs/plugin/relativeTime';

import Avatar from 'components/Avatar';
import * as actions from 'containers/App/actions';
import { CommentActionButton } from './Checklist.ActionButton';

dayjs.extend(relativeTime);

const ChecklistCommentItem = ({
  id,
  meId,
  html,
  createdAt,
  updatedAt,
  login,
  onEdit,
  onDelete,
}) => {
  const [edit, setOnEdit] = useState(false);
  const [content, setContent] = useState(html);
  const dateFormat = useSelector((state) => state.global.userInfo.organization.datePreference);

  const onEditComment = () => {
    setOnEdit(true);
  };

  const onDeleteComment = () => {
    onDelete(id);
  };

  const onSave = (e) => {
    e.nativeEvent.stopImmediatePropagation();
    setOnEdit(false);
    onEdit(id, content);
  };

  const onCancel = (e) => {
    e.nativeEvent.stopImmediatePropagation();
    setOnEdit(false);
  };

  const { firstname, lastname, profileImage, id: userId } = login;
  const profileImagePath = profileImage?.resolutions?.full?.path;
  const timeAgo = dayjs(createdAt).fromNow();
  const edited = updatedAt ? '(edited)' : '';

  const renderAvatar = profileImagePath ? (
    <Avatar
      photo={`//${process.env.REACT_APP_AWS_CLOUDFRONT}/${profileImagePath}`}
      size={50}
      borderSize={3}
      className="APCommentView-avatar"
    />
  ) : (
    <Avatar
      size={50}
      borderSize={3}
      text={firstname.charAt(0) + lastname.charAt(0)}
      className="APCommentView-avatar"
    />
  );

  return (
    <>
      <div name="container">
        {renderAvatar}
        <div name="content">
          <div className="APCommentView-commenter">
            <div name="author-name">{firstname} {lastname}</div>
            <div
              name="date"
              // #TODO: Need to format this to long format.
              title={dayjs(createdAt).format(dateFormat ? `${dateFormat.toUpperCase()} h:ma` : 'MM/DD/YYYY')}
            >
              {timeAgo} {edited}
            </div>
          </div>
          {edit ? (
            <>
              <textarea
                name="details"
                className="edit-area"
                value={content.replace(/<br>/gi, '\n')}
                onChange={(e) => {
                  setContent(e.target.value);
                }}
              />
              <button
                className="APButton small save"
                onClick={onSave}
              >
                save
              </button>
              <button
                className="APButton small alternate cancel"
                onClick={onCancel}
              >
                cancel
              </button>
            </>
          ) : (
            <div name="body">{ parse(html) }</div>
          )}
        </div>
      </div>
      {meId === userId && (
        <div className="APIconButton nc-checklist__action-button select-button">
          <CommentActionButton
            className="doogle"
            onEdit={onEditComment}
            onDelete={onDeleteComment}
          />
        </div>
      )}
    </>
  );
};

const ChecklistComments = ({
  comments,
  taskId,
  meId,
}) => {
  const dispatch = useDispatch();
  const ref = useRef(null);
  const {
    createTaskComment,
    updateTaskComment,
    deleteTaskComment,
  } = bindActionCreators(actions, dispatch);
  const [focused, setFocused] = useState(false);
  const [draftComment, setDraftComment] = useState('');

  useEffect(() => {
    if (document.activeElement === ref.current) {
      setFocused(true);
    } else {
      setFocused(false);
    }
  }, []);

  useEffect(() => {
    if (ref.current) {
      ref.current.style.height = '0px';
      const { scrollHeight } = ref.current;

      ref.current.style.height = `${scrollHeight}px`;
    }
  }, [ref, draftComment]);

  const onAdd = (e) => {
    e.nativeEvent.stopImmediatePropagation();
    const variables = {
      input: {
        body: draftComment,
      },
      taskId,
    };
    createTaskComment({ variables });
    ref.current.value = '';
    setDraftComment('');
  };

  const onEdit = (id, content) => {
    const variables = {
      input: {
        body: content,
      },
      commentId: id,
      taskId,
    };
    updateTaskComment({ variables });
  };

  const onDelete = (commentId) => {
    const variables = {
      commentId,
      taskId,
    };
    deleteTaskComment({ variables });
  };

  const changeComment = (e) => {
    setDraftComment(e.target.value);
  };

  return (
    <div className="nc-checklist__comments">
      <div name="comments">
        {comments?.map((comment) => (
          <div
            className="APCommentView"
            key={`comment_${comment.id}`}
          >
            <ChecklistCommentItem
              meId={meId}
              onEdit={onEdit}
              onDelete={onDelete}
              {...comment}
            />
          </div>
        ))}
      </div>
      <div className="nc-checklist__comments--add">
        <textarea
          ref={ref}
          value={draftComment}
          onChange={changeComment}
          placeholder="Enter a comment"
        />
        {(focused || draftComment) && (
          <button onClick={onAdd}>
            ADD
          </button>
        )}
      </div>
    </div>
  );
};

export default ChecklistComments;
